import { createSlice } from '@reduxjs/toolkit';
import _, { isEmpty } from 'lodash';
import { saveAgylePreference } from './preferences';
import { GLOBAL_PANEL_ACTION_TYPE } from '../../components/actionPanel/components/panelType';
import { MAP_ZOOM_LEVEL } from '../../util/mapCreator/mapUtils';
import { getConfig } from '../../util/configUtil';
import { DEFAULT_TOOLTIP_STATE } from '../../pages/ward/map/components/cardUtil/cardUtil';

const INIT_OPEN_STATE = {
    [GLOBAL_PANEL_ACTION_TYPE.WARD_FILTERS]: false,
    [GLOBAL_PANEL_ACTION_TYPE.WARD_ACTION]: false,
    [GLOBAL_PANEL_ACTION_TYPE.WARD_SPECIALTY]: false,
    [GLOBAL_PANEL_ACTION_TYPE.WARD_MULTISELECT]: false,
    [GLOBAL_PANEL_ACTION_TYPE.WARD_ESCALATION]: false,
    [GLOBAL_PANEL_ACTION_TYPE.ADMITTING_CONSULTANT]: false,
    [GLOBAL_PANEL_ACTION_TYPE.EXTERNAL_LINKS]: false,
};

export const WARD_INIT_FILTERS = {
    noReferrals: false,
    referrals: [],
    dischargeStatus: [],
    speciality: [],
    timestamp: [],
    onlyPaeds: false,
    walkType: 'all',
    searchText: '',
};

export const wardMap = createSlice({
    name: 'wardMap',
    initialState: {
        holdingList: [],
        multiSelectList: [],
        multiSelectMode: false,
        showHoldingList: false,
        showOffWardList: false,
        disableMapMove: false,
        scrollingPending: false,
        selectPatientEvent: undefined,
        contextMenuOpen: undefined,
        wardViewType: 'map',
        forceProcessBoardType: null,
        hideGender: false,
        resetZoom: false,
        wardSearchPatient: null,
        filters: WARD_INIT_FILTERS,
        directAdmission: undefined,
        forceReload: false,
        patientToTransfer: null,
        patientListSort: [],
        customCleaningMapOption: undefined,
        processBoardExpanded: {},
        openState: INIT_OPEN_STATE,
        layouts: [],
        activeLayout: null,
        wardMapObject: null,
        mapNoMoved: false,
        zoneSelected: null,
        lobbyExpanded: false,

        // Zoom level
        zoomLevel: MAP_ZOOM_LEVEL.BIG,
        zoomLevelScale: null,
        isOpenZoomLevelModal: false,
        tempZoomScale: null,

        // Patient card tooltip
        patientCardTooltip: DEFAULT_TOOLTIP_STATE,
    },
    reducers: {
        setHoldingList: (state, ward) => {
            state.holdingList = ward.payload;
        },
        setShowHoldingList: (state, ward) => {
            state.showHoldingList = ward.payload;
        },
        setShowOffWardList: (state, ward) => {
            state.showOffWardList = ward.payload;
        },
        setDisableMapMove: (state, ward) => {
            state.disableMapMove = ward.payload;
        },
        setScrollingPending: (state, ward) => {
            state.scrollingPending = ward.payload;
        },
        setSelectPatientEvent: (state, ward) => {
            state.selectPatientEvent = ward.payload;
        },
        setWardViewType: (state, ward) => {
            state.wardViewType = ward.payload;
        },
        setForceProcessBoardType: (state, ward) => {
            state.forceProcessBoardType = ward.payload;
        },
        setHideGender: (state, ward) => {
            state.hideGender = ward.payload;
        },
        setContextMenuOpen: (state, ward) => {
            state.contextMenuOpen = ward.payload;
        },
        setFilter: (state, ward) => {
            state.filters = {
                ...state.filters,
                ...ward.payload,
            };
        },
        setOpenWardState: (state, ward) => {
            state.openState = {
                ...state.openState,
                ...ward.payload,
            };
        },
        setResetZoom: (state, ward) => {
            state.resetZoom = ward.payload;
        },
        setWardSearchPatient: (state, ward) => {
            state.wardSearchPatient = ward.payload;
        },
        setMultiSelectList: (state, ward) => {
            state.multiSelectList = ward.payload;
        },
        setMultiSelectMode: (state, ward) => {
            state.multiSelectMode = ward.payload;
        },
        setDirectAdmission: (state, ward) => {
            state.directAdmission = ward.payload;
        },
        setForceReload: (state, ward) => {
            state.forceReload = ward.payload;
        },
        setPatientToTransfer: (state, ward) => {
            state.patientToTransfer = ward.payload;
        },
        setPatientListSort: (state, ward) => {
            state.patientListSort = ward.payload;
        },
        setCustomCleaningMapOption: (state, ward) => {
            state.customCleaningMapOption = ward.payload;
        },
        setProcessBoardExpanded: (state, ward) => {
            state.processBoardExpanded = ward.payload;
        },
        setMapLayouts: (state, ward) => {
            state.layouts = ward.payload;
        },
        setMapActiveLayout: (state, ward) => {
            state.activeLayout = ward.payload;
        },
        setWardMapObject: (state, ward) => {
            state.wardMapObject = ward.payload;
        },
        setWardMapNoMoved: (state, ward) => {
            state.mapNoMoved = ward.payload;
        },
        setWardMapZoneSelected: (state, ward) => {
            state.zoneSelected = ward.payload;
        },
        setLobbyExpanded: (state, ward) => {
            state.lobbyExpanded = ward.payload;
        },
        setZoomLevel: (state, ward) => {
            state.zoomLevel = ward.payload;
        },
        setOpenZoomLevelModal: (state, ward) => {
            state.isOpenZoomLevelModal = ward.payload;
        },
        setTempZoomScale: (state, ward) => {
            state.tempZoomScale = ward.payload;
        },
        setZoomLevelScale: (state, ward) => {
            state.zoomLevelScale = ward.payload;
        },
        setPatientCardTooltip: (state, ward) => {
            state.patientCardTooltip = {
                ...state.patientCardTooltip,
                ...ward.payload,
            };
        },
        resetPatientCardTooltip: (state) => {
            state.patientCardTooltip = DEFAULT_TOOLTIP_STATE;
        },
    },
});

// Setters
export const {
    setHoldingList,
    setShowHoldingList,
    setDisableMapMove,
    setScrollingPending,
    setSelectPatientEvent,
    setWardViewType,
    setForceProcessBoardType,
    setContextMenuOpen,
    setHideGender,
    setFilter,
    setResetZoom,
    setWardSearchPatient,
    setMultiSelectList,
    setMultiSelectMode,
    setDirectAdmission,
    setForceReload,
    setPatientToTransfer,
    setShowOffWardList,
    setPatientListSort,
    setCustomCleaningMapOption,
    setProcessBoardExpanded,
    setOpenWardState,
    setMapLayouts,
    setMapActiveLayout,
    setWardMapObject,
    setWardMapNoMoved,
    setWardMapZoneSelected,
    setLobbyExpanded,
    setZoomLevel,
    setOpenZoomLevelModal,
    setTempZoomScale,
    setPatientCardTooltip,
    resetPatientCardTooltip,
    setZoomLevelScale,
} = wardMap.actions;

// Variable
export const holdingList = (state) => state.wardMap.holdingList;
export const showHoldingList = (state) => state.wardMap.showHoldingList;
export const showOffWardList = (state) => state.wardMap.showOffWardList;
export const disableMapMove = (state) => state.wardMap.disableMapMove;
export const scrollingPending = (state) => state.wardMap.scrollingPending;
export const selectPatientEvent = (state) => state.wardMap.selectPatientEvent;
export const wardViewType = (state) => state.wardMap.wardViewType;
export const forceProcessBoardType = (state) => state.wardMap.forceProcessBoardType;
export const contextMenuOpen = (state) => state.wardMap.contextMenuOpen;
export const wardMapFilters = (state) => state.wardMap.filters;
export const hideGender = (state) => state.wardMap.hideGender;
export const isResetZoom = (state) => state.wardMap.resetZoom;
export const wardSearchPatient = (state) => state.wardMap.wardSearchPatient;
export const wardMultiSelectList = (state) => state.wardMap.multiSelectList;
export const wardMultiSelectMode = (state) => state.wardMap.multiSelectMode;
export const wardDirectAdmission = (state) => state.wardMap.directAdmission;
export const wardForceReload = (state) => state.wardMap.forceReload;
export const wardPatientToTransfer = (state) => state.wardMap.patientToTransfer;
export const wardPatientListSort = (state) => state.wardMap.patientListSort;
export const processBoardExpanded = (state) => state.wardMap.processBoardExpanded;
export const wardOpenState = (state) => state.wardMap.openState;
export const wardLayouts = (state) => state.wardMap.layouts;
export const wardActiveLayout = (state) => state.wardMap.activeLayout;
export const wardMapObject = (state) => state.wardMap.wardMapObject;
export const wardMapNoMoved = (state) => state.wardMap.mapNoMoved;
export const wardMapZoneSelected = (state) => state.wardMap.zoneSelected;
export const wardMapLobbyExpanded = (state) => state.wardMap.lobbyExpanded;
export const wardMapZoomLevel = (state) => state.wardMap.zoomLevel;
export const wardMapZoomLevelModal = (state) => state.wardMap.isOpenZoomLevelModal;
export const wardMapTempZoomScale = (state) => state.wardMap.tempZoomScale;
export const wardMapPatientCardTooltip = (state) => state.wardMap.patientCardTooltip;
export const wardMapZoomLevelScale = (state) => state.wardMap.zoomLevelScale;

// Functions
export const getWardsIdForPatientsSearch =
    (wardIdArray = [], forceHospital = null) =>
    (dispatch, getState) => {
        return wardIdArray;
    };

export const clearWardOpenState = () => (dispatch, getState) => {
    dispatch(setOpenWardState(INIT_OPEN_STATE));
};

/**
 * Updates the last visited ward in the user's preferences.
 *
 * @param {string} wardId - The ID of the ward to set as last visited.
 * @returns {Function} A thunk that dispatches the updated preferences.
 */
export const setLastVisitMap = (wardId) => (dispatch, getState) => {
    if (!wardId) return;

    const agylePreference = getState().preferences.agyle;
    let lastVisit = [...new Set(agylePreference?.wardMap?.lastVisit || [])];
    _.remove(lastVisit, (id) => id === wardId);
    lastVisit.unshift(wardId);

    if (lastVisit.length > 5) {
        lastVisit = lastVisit.slice(0, 5);
    }

    dispatch(
        saveAgylePreference({
            wardMap: {
                ...agylePreference.wardMap,
                lastVisit,
            },
        }),
    );
};

export const hasBadMultiPatient = (bedId) => (dispatch, getState) => {
    const patients = getState().ward.patients ?? [];
    return patients.filter((patient) => patient.bed === bedId)?.length > 1;
};

export const updateZoomLevel = (scale) => (dispatch, getState) => {
    const map = getState().wardMap.wardMapObject;
    const currentZoomLevel = getState().wardMap.zoomLevel;
    const moduleEnabled = getConfig('modules.map.zoomLevelsEnabled', false);
    dispatch(setZoomLevelScale(scale));

    if (!moduleEnabled) {
        if (currentZoomLevel !== MAP_ZOOM_LEVEL.FULL) {
            dispatch(setZoomLevel(MAP_ZOOM_LEVEL.FULL));
        }
        return;
    }

    if (map?.metadata?.zoomLevel) {
        const enabledLevels = map.metadata.zoomLevel.filter((level) => level.value !== null);

        if (isEmpty(enabledLevels)) {
            dispatch(setZoomLevel(MAP_ZOOM_LEVEL.FULL));
            return;
        }

        // Find the highest zoom level that is less than or equal to the current scale
        const matchingLevel = enabledLevels.filter((level) => scale >= level.value).sort((a, b) => b.value - a.value)[0];

        let newZoomLevel;
        if (matchingLevel) {
            newZoomLevel = matchingLevel?.level;
        } else {
            const lowestLevel = enabledLevels.sort((a, b) => a.value - b.value)[0];
            newZoomLevel = lowestLevel?.level;
        }

        if (newZoomLevel !== currentZoomLevel) {
            dispatch(setZoomLevel(newZoomLevel));
        }
    } else if (currentZoomLevel !== MAP_ZOOM_LEVEL.FULL) {
        dispatch(setZoomLevel(MAP_ZOOM_LEVEL.FULL));
    }
};
export default wardMap.reducer;
